import React, { useState, useEffect, useRef } from 'react';
import { fetchSubtitle, parseSRT } from '../services/api';

interface SubtitleData {
  start: number;
  end: number;
  text: string;
}

interface AudioSubtitlePlayerProps {
  audioUrl?: string;
  subtitleUrl?: string;
  active: boolean;
  onSubtitleChange: (subtitle: string) => void;
  paused: boolean;
  onPausedChange: (paused: boolean) => void;
  playbackRate?: number;
  postId?: number; // Add postId prop for better tracking
}

// ✅ CRITICAL FIX: Enhanced Global Audio Manager with Post ID tracking and Duplicate Handling
class GlobalAudioManager {
  private static instance: GlobalAudioManager;
  private currentAudio: HTMLAudioElement | null = null;
  private currentPlayerId: string | null = null;
  private currentPostId: number | null = null;
  private allAudioInstances: Map<string, { audio: HTMLAudioElement; postId: number; active: boolean }> = new Map();
  private postToPlayerMapping: Map<number, Set<string>> = new Map(); // Track multiple players per post

  static getInstance(): GlobalAudioManager {
    if (!GlobalAudioManager.instance) {
      GlobalAudioManager.instance = new GlobalAudioManager();
    }
    return GlobalAudioManager.instance;
  }

  setActiveAudio(audio: HTMLAudioElement, playerId: string, postId: number): void {
    // ✅ CRITICAL FIX: Stop ALL audio instances for ALL posts first (prevent cross-post audio bleed)
    this.stopAllAudio();
    
    // Update registry
    this.allAudioInstances.set(playerId, { audio, postId, active: true });
    
    // Track post to player mapping for duplicate detection
    if (!this.postToPlayerMapping.has(postId)) {
      this.postToPlayerMapping.set(postId, new Set());
    }
    this.postToPlayerMapping.get(postId)!.add(playerId);
    
    // Set as current
    this.currentAudio = audio;
    this.currentPlayerId = playerId;
    this.currentPostId = postId;
    
    console.log('[GlobalAudioManager] 🎵 Set active audio:', { 
      playerId, 
      postId, 
      totalInstances: this.allAudioInstances.size,
      playersForThisPost: this.postToPlayerMapping.get(postId)?.size || 0,
      audioSrc: audio.src?.split('/').pop() || 'unknown'
    });

    // ✅ NEW: If there are multiple players for the same post, stop the others
    if (this.postToPlayerMapping.get(postId)!.size > 1) {
      console.log('[GlobalAudioManager] ⚠️ Multiple players detected for post:', postId, 'stopping duplicates');
      this.stopDuplicatePlayersForPost(postId, playerId);
    }
  }

  // ✅ NEW: Stop duplicate players for the same post (parallel loading issue fix)
  private stopDuplicatePlayersForPost(postId: number, exceptPlayerId: string): void {
    const playersForPost = this.postToPlayerMapping.get(postId);
    if (!playersForPost) return;

    let stoppedCount = 0;
    playersForPost.forEach(playerIdToCheck => {
      if (playerIdToCheck !== exceptPlayerId) {
        const instance = this.allAudioInstances.get(playerIdToCheck);
        if (instance && !instance.audio.paused) {
          instance.audio.pause();
          instance.audio.currentTime = 0;
          instance.active = false;
          stoppedCount++;
          console.log('[GlobalAudioManager] 🔇 Stopped duplicate player:', { 
            playerId: playerIdToCheck, 
            postId: instance.postId 
          });
        }
      }
    });

    if (stoppedCount > 0) {
      console.log('[GlobalAudioManager] 🧹 Stopped', stoppedCount, 'duplicate players for post:', postId);
    }
  }

  stopAllOtherAudio(exceptPlayerId?: string): void {
    let stoppedCount = 0;
    
    // Stop current audio if different
    if (this.currentAudio && this.currentPlayerId !== exceptPlayerId) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      stoppedCount++;
      console.log('[GlobalAudioManager] 🔇 Stopped current audio:', this.currentPlayerId);
    }
    
    // Stop all registered instances
    this.allAudioInstances.forEach((instance, id) => {
      if (id !== exceptPlayerId && !instance.audio.paused) {
        instance.audio.pause();
        instance.audio.currentTime = 0;
        instance.active = false;
        stoppedCount++;
        console.log('[GlobalAudioManager] 🔇 Stopped registered audio:', { 
          playerId: id, 
          postId: instance.postId 
        });
      }
    });
    
    // Also force stop any DOM audio elements not in registry (except background music)
    const allDOMElements = document.querySelectorAll('audio');
    allDOMElements.forEach((audio) => {
      // ✅ CRITICAL FIX: Don't stop background music or continuous playback audio
      const isBackgroundMusic = audio.getAttribute('data-background-music') === 'true';
      const isContinuousPlayback = audio.getAttribute('data-continuous-playback') === 'true';
      
      if (!audio.paused && !isBackgroundMusic && !isContinuousPlayback) {
        audio.pause();
        audio.currentTime = 0;
        stoppedCount++;
        console.log('[GlobalAudioManager] 🔇 Stopped unregistered DOM audio:', audio.src?.split('/').pop());
      } else if (!audio.paused && (isBackgroundMusic || isContinuousPlayback)) {
        console.log('[GlobalAudioManager] 🎵 Preserved background/continuous audio:', audio.src?.split('/').pop());
      }
    });
    
    if (stoppedCount > 0) {
      console.log('[GlobalAudioManager] 🧹 Total audio instances stopped:', stoppedCount);
    }
  }

  // ✅ NEW: Stop ALL audio (for use when switching between posts)
  stopAllAudio(): void {
    this.stopAllOtherAudio();
    this.currentAudio = null;
    this.currentPlayerId = null;
    this.currentPostId = null;
  }

  stopCurrentAudio(): void {
    this.stopAllOtherAudio();
    this.currentAudio = null;
    this.currentPlayerId = null;
    this.currentPostId = null;
  }

  isCurrentAudio(playerId: string): boolean {
    return this.currentPlayerId === playerId;
  }

  isCurrentPostAudio(postId: number): boolean {
    return this.currentPostId === postId;
  }

  unregisterAudio(playerId: string): void {
    const instance = this.allAudioInstances.get(playerId);
    if (instance) {
      if (!instance.audio.paused) {
        instance.audio.pause();
        instance.audio.currentTime = 0;
      }
      
      // Remove from post mapping
      const postId = instance.postId;
      const playersForPost = this.postToPlayerMapping.get(postId);
      if (playersForPost) {
        playersForPost.delete(playerId);
        if (playersForPost.size === 0) {
          this.postToPlayerMapping.delete(postId);
        }
      }

      this.allAudioInstances.delete(playerId);
      
      // Clear current if this was the current
      if (this.currentPlayerId === playerId) {
        this.currentPlayerId = null;
        this.currentPostId = null;
        this.currentAudio = null;
      }
      
      console.log('[GlobalAudioManager] 🗑️ Unregistered audio:', { 
        playerId, 
        postId: instance.postId,
        remainingInstances: this.allAudioInstances.size,
        remainingPlayersForPost: this.postToPlayerMapping.get(postId)?.size || 0
      });
    }
  }

  // ✅ DEBUG: Enhanced methods for testing and debugging
  getCurrentPlayerId(): string | null {
    return this.currentPlayerId;
  }

  getCurrentPostId(): number | null {
    return this.currentPostId;
  }

  getAllAudioInfo(): any {
    const instances = Array.from(this.allAudioInstances.entries()).map(([id, instance]) => ({
      playerId: id,
      postId: instance.postId,
      active: instance.active,
      paused: instance.audio.paused,
      src: instance.audio.src?.split('/').pop() || 'unknown'
    }));

    const postMappings = Array.from(this.postToPlayerMapping.entries()).map(([postId, playerIds]) => ({
      postId,
      playerCount: playerIds.size,
      playerIds: Array.from(playerIds)
    }));

    return {
      currentPlayerId: this.currentPlayerId,
      currentPostId: this.currentPostId,
      currentAudioSrc: this.currentAudio?.src?.split('/').pop() || null,
      currentAudioPaused: this.currentAudio?.paused || null,
      totalInstances: this.allAudioInstances.size,
      instances,
      postMappings
    };
  }

  // ✅ NEW: Enhanced cleanup method for virtual scrolling and parallel loading
  cleanupVirtualScrolling(): void {
    console.log('[GlobalAudioManager] 🧹 Virtual scrolling cleanup initiated');
    
    // Stop all audio
    this.stopAllAudio();
    
    // Clear registry of inactive instances
    const activeInstances = new Map();
    const activePostMappings = new Map();
    
    this.allAudioInstances.forEach((instance, id) => {
      if (instance.active && document.contains(instance.audio)) {
        activeInstances.set(id, instance);
        
        // Rebuild post mapping for active instances
        if (!activePostMappings.has(instance.postId)) {
          activePostMappings.set(instance.postId, new Set());
        }
        activePostMappings.get(instance.postId).add(id);
      } else {
        console.log('[GlobalAudioManager] 🗑️ Removing stale instance:', { playerId: id, postId: instance.postId });
      }
    });
    
    this.allAudioInstances = activeInstances;
    this.postToPlayerMapping = activePostMappings;
    
    console.log('[GlobalAudioManager] ✅ Virtual scrolling cleanup complete:', {
      remainingInstances: this.allAudioInstances.size,
      postsWithMultiplePlayers: Array.from(this.postToPlayerMapping.entries()).filter(([, players]) => players.size > 1).length
    });
  }

  // ✅ NEW: Force cleanup for post switching (called from TikTokFeed)
  onPostSwitch(newPostId: number): void {
    console.log('[GlobalAudioManager] 🔄 Post switch detected:', { from: this.currentPostId, to: newPostId });
    
    // If switching to a different post, stop all audio
    if (this.currentPostId !== newPostId) {
      this.stopAllAudio();
      
      // Deactivate all instances that are not for the new post
      this.allAudioInstances.forEach((instance, playerId) => {
        if (instance.postId !== newPostId) {
          instance.active = false;
          if (!instance.audio.paused) {
            instance.audio.pause();
            instance.audio.currentTime = 0;
          }
        }
      });
      
      console.log('[GlobalAudioManager] 🧹 Post switch cleanup complete for post:', newPostId);
    }
  }
}

// ✅ CRITICAL: Export for global access (debugging and testing)
declare global {
  interface Window {
    GlobalAudioManager: typeof GlobalAudioManager;
  }
}

if (typeof window !== 'undefined') {
  window.GlobalAudioManager = GlobalAudioManager;
}

const AudioSubtitlePlayer: React.FC<AudioSubtitlePlayerProps> = ({
  audioUrl,
  subtitleUrl,
  active,
  onSubtitleChange,
  paused,
  onPausedChange,
  playbackRate = 1.0,
  postId
}) => {
  const [subtitles, setSubtitles] = useState<SubtitleData[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);
  const intervalRef = useRef<number | null>(null);
  
  // ✅ NEW: Generate unique player ID and get global audio manager
  const playerIdRef = useRef(`audio-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const audioManager = GlobalAudioManager.getInstance();
  
  // ✅ NEW: Save playback position for out-screen resume
  const savedPlaybackPositionRef = useRef<number>(0);
  
  // ✅ Extract postId from audioUrl if not provided
  const extractedPostId = postId || (audioUrl ? parseInt(audioUrl.split('/').pop()?.split('.')[0] || '0', 10) : 0);

  // ✅ CRITICAL FIX: Enhanced active state handling with duplicate prevention
  useEffect(() => {
    if (!active) {
      // Stop this audio via global manager
      if (audioManager.isCurrentAudio(playerIdRef.current)) {
        audioManager.stopCurrentAudio();
      }
      
      // Local cleanup
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      // Clear subtitle update interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Clear subtitle display
      onSubtitleChange('');
      
      // ✅ REDUCED LOGGING: Only log cleanup in dev mode
      if (process.env.NODE_ENV === 'development') {
        console.log('[AudioSubtitlePlayer] 🔇 Cleanup:', playerIdRef.current);
      }
      return;
    }

    // ✅ CRITICAL FIX: Only register if we have audio and this is really becoming active
    if (audioRef.current && audioUrl && active) {
      // Check if there's already an active player for this post
      const audioInfo = audioManager.getAllAudioInfo();
      const existingPlayersForPost = audioInfo.postMappings.find((pm: any) => pm.postId === extractedPostId);
      
      if (existingPlayersForPost && existingPlayersForPost.playerCount > 0) {
        console.log('[AudioSubtitlePlayer] ⚠️ Multiple players detected for post:', extractedPostId, 'existing players:', existingPlayersForPost.playerCount);
      }

      audioManager.setActiveAudio(audioRef.current, playerIdRef.current, extractedPostId);
      
      // ✅ REDUCED LOGGING: Only log activation in dev mode or for debugging
      if (process.env.NODE_ENV === 'development' || existingPlayersForPost?.playerCount > 0) {
        console.log('[AudioSubtitlePlayer] 🎵 Activated:', { 
          playerId: playerIdRef.current,
          postId: extractedPostId,
          existingPlayers: existingPlayersForPost?.playerCount || 0
        });
      }
    }

    // Reset paused state when becoming active
    onPausedChange(false);
  }, [active, onPausedChange, onSubtitleChange, audioUrl, subtitleUrl, audioManager, extractedPostId]);

  // Load subtitles only when active
  useEffect(() => {
    if (subtitleUrl && active) {
      fetchSubtitle(subtitleUrl)
        .then(data => {
          const parsed = parseSRT(data);
          setSubtitles(parsed);
          // ✅ REDUCED LOGGING: Only log subtitles in dev mode
        if (process.env.NODE_ENV === 'development') {
          console.log('[AudioSubtitlePlayer] 📝 Subtitles loaded:', parsed.length);
        }
        })
        .catch(error => {
          console.error('[AudioSubtitlePlayer] ❌ Error loading subtitles:', error);
          setSubtitles([]);
        });
    } else {
      setSubtitles([]);
    }
  }, [subtitleUrl, active]);

  // Update playback rate when prop changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // ✅ IMPROVED: Audio playback and subtitle sync with Global Audio Manager
  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (active && audioRef.current && audioUrl) {
      // ✅ CRITICAL FIX: Only play if this is the current audio in global manager
      if (audioManager.isCurrentAudio(playerIdRef.current)) {
        if (!paused) {
          audioRef.current.play().catch((error) => {
            console.log('[AudioSubtitlePlayer] ⚠️ Autoplay blocked, marking as paused:', error);
            onPausedChange(true);
          });
        } else {
          audioRef.current.pause();
        }
        
        const updateSubtitle = () => {
          if (!audioRef.current || paused || !active || !audioManager.isCurrentAudio(playerIdRef.current)) return;
          
          const currentTime = audioRef.current.currentTime;
          const currentSub = subtitles.find(
            sub => currentTime >= sub.start && currentTime <= sub.end
          );
          onSubtitleChange(currentSub ? currentSub.text : '');
        };
        
        if (!paused) {
          intervalRef.current = window.setInterval(updateSubtitle, 100);
        }
      } else {
        // Not the current audio, pause it
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        onSubtitleChange('');
      }
      
      return () => { 
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else if (!active && audioRef.current) {
      // Force cleanup when not active
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      onSubtitleChange('');
    }
  }, [active, subtitles, paused, onSubtitleChange, onPausedChange, audioUrl, audioManager]);

  // ✅ IMPROVED: Handle audio ended - simple loop with active check and Global Audio Manager
  const handleAudioLoop = () => {
    if (audioRef.current && !paused && active && audioManager.isCurrentAudio(playerIdRef.current)) {
      // Loop audio hiện tại only if still active and is current audio
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        onPausedChange(true);
      });
      onSubtitleChange('');
      // ✅ REDUCED LOGGING: Only log audio loop in dev mode
      if (process.env.NODE_ENV === 'development') {
        console.log('[AudioSubtitlePlayer] 🔁 Audio looped:', playerIdRef.current);
      }
    }
  };

  // ✅ CRITICAL FIX: Cleanup on unmount with Global Audio Manager + out-screen resume handler
  useEffect(() => {
    // ✅ NEW: Listen for global cleanup events from TikTokFeed
    const handleForceCleanup = (event: CustomEvent) => {
      console.log('[AudioSubtitlePlayer] 🧹 Force cleanup triggered:', event.detail, playerIdRef.current);
      
      // ✅ SAVE POSITION: Save current playback position before cleanup (for out-screen resume)
      if (audioRef.current && !audioRef.current.paused && audioRef.current.currentTime > 0) {
        savedPlaybackPositionRef.current = audioRef.current.currentTime;
        console.log('[AudioSubtitlePlayer] 💾 Saved playback position:', savedPlaybackPositionRef.current);
      }
      
      // Unregister from global manager (this will also stop the audio)
      audioManager.unregisterAudio(playerIdRef.current);
      
      // Clear intervals
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Clear subtitle
      onSubtitleChange('');
    };

    // ✅ NEW: Listen for out-screen resume events from TikTokFeed 
    const handleForceResume = (event: CustomEvent) => {
      const detail = event.detail;
      console.log('[AudioSubtitlePlayer] 🔄 Force resume triggered:', detail, 'playerPostId:', extractedPostId);
      
      // Only handle if this is the active post's audio player
      if (active && extractedPostId === detail.postId && audioRef.current && audioUrl) {
        console.log('[AudioSubtitlePlayer] ▶️ Attempting to resume from out-screen return:', playerIdRef.current);
        
        // Force resume if this is the current audio in global manager
        if (audioManager.isCurrentAudio(playerIdRef.current)) {
          // ✅ RESTORE POSITION: Set saved playback position before resume
          if (savedPlaybackPositionRef.current > 0) {
            audioRef.current.currentTime = savedPlaybackPositionRef.current;
            console.log('[AudioSubtitlePlayer] 🔄 Restored playback position:', savedPlaybackPositionRef.current);
            // Clear saved position after restore
            savedPlaybackPositionRef.current = 0;
          }
          
          audioRef.current.play()
            .then(() => {
              onPausedChange(false);
              console.log('[AudioSubtitlePlayer] ✅ Resumed successfully from out-screen at position:', audioRef.current?.currentTime);
            })
            .catch(error => {
              console.log('[AudioSubtitlePlayer] ⚠️ Resume failed, staying paused:', error);
              onPausedChange(true);
            });
        } else {
          // Re-register as active audio and try to play
          audioManager.setActiveAudio(audioRef.current, playerIdRef.current, extractedPostId);
          setTimeout(() => {
            if (audioRef.current && audioManager.isCurrentAudio(playerIdRef.current)) {
              // ✅ RESTORE POSITION: Set saved playback position before resume
              if (savedPlaybackPositionRef.current > 0) {
                audioRef.current.currentTime = savedPlaybackPositionRef.current;
                console.log('[AudioSubtitlePlayer] 🔄 Restored playback position after re-register:', savedPlaybackPositionRef.current);
                // Clear saved position after restore
                savedPlaybackPositionRef.current = 0;
              }
              
              audioRef.current.play()
                .then(() => {
                  onPausedChange(false);
                  console.log('[AudioSubtitlePlayer] ✅ Re-registered and resumed from out-screen at position:', audioRef.current?.currentTime);
                })
                .catch(error => {
                  console.log('[AudioSubtitlePlayer] ⚠️ Re-register resume failed:', error);
                  onPausedChange(true);
                });
            }
          }, 100);
        }
      }
    };
    
    // Add event listeners
    window.addEventListener('forceAudioCleanup', handleForceCleanup as EventListener);
    window.addEventListener('forceResumeActiveVideo', handleForceResume as EventListener);
    
    return () => {
      // Clean up event listeners
      window.removeEventListener('forceAudioCleanup', handleForceCleanup as EventListener);
      window.removeEventListener('forceResumeActiveVideo', handleForceResume as EventListener);
      
      // Unregister from global manager (this will also stop the audio)
      audioManager.unregisterAudio(playerIdRef.current);
      
      // Original cleanup
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      console.log('[AudioSubtitlePlayer] 🧹 Component unmounted, cleaned up:', playerIdRef.current);
    };
  }, [onSubtitleChange, audioManager, active, extractedPostId, audioUrl, onPausedChange]);

  if (!audioUrl) return null;

  // ✅ ENHANCED LAZY AUDIO ELEMENT: Ultra-conservative loading to prevent ANY requests during scroll
  return (
    <audio 
      ref={audioRef}
      src={active && !paused ? audioUrl : undefined} // ✅ Only set src when active AND not paused
      controls 
      style={{ display: 'none' }}
      onEnded={handleAudioLoop}
      preload="none" // ✅ Prevent any automatic loading
      onLoadStart={(e) => {
        // ✅ SPAM PREVENTION: Only allow loading if truly active and playing
        if (!active || paused) {
          e.preventDefault();
          const audio = e.target as HTMLAudioElement;
          audio.src = '';
          console.log('[AudioSubtitlePlayer] 🚫 Prevented unwanted audio load during scroll');
        }
      }}
    />
  );
};

export default AudioSubtitlePlayer; 